(() => {
  if (window.blendFooterTransition) return;

  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const surfaceSelector = [
    ':scope > .shopify-section',
    ':scope > .shopify-section > :not(style, script, link)',
    ':scope > .shopify-section > .isolate > .rich-text',
    ':scope > .shopify-section > .gradient > .isolate',
  ].join(', ');
  let mainContent;
  let entry;
  let cta;
  let footer;
  let watermarkBottom = null;
  let frameId = null;
  let refreshNeeded = true;
  let isActive = false;

  // Only section canvases participate. Cards, media, inputs and the black footer
  // are deliberately outside this shallow selection.
  const collectSurfaces = () => {
    if (!mainContent) return;
    mainContent.querySelectorAll(surfaceSelector).forEach((surface) => {
      if (surface.hasAttribute('data-blend-footer-surface')) return;
      const style = getComputedStyle(surface);
      if (style.backgroundImage !== 'none') return;
      const channels = style.backgroundColor.match(/[\d.]+/g)?.map(Number);
      if (!channels || channels.length < 3) return;
      const [red, green, blue, alpha = 1] = channels;
      const isClear = alpha === 0;
      const isWhite = Math.min(red, green, blue) >= 240 && Math.max(red, green, blue) - Math.min(red, green, blue) <= 10;
      if (isClear || isWhite) surface.setAttribute('data-blend-footer-surface', '');
    });
  };

  // A footer CTA may continue an existing white canvas, but must never introduce one.
  const syncCtaSurface = () => {
    if (!cta || !mainContent) return;
    const pageWidth = mainContent.getBoundingClientRect().width;
    const candidates = [mainContent, ...mainContent.querySelectorAll(surfaceSelector)];
    const hasWhiteSection = candidates.some((surface) => {
      const rect = surface.getBoundingClientRect();
      if (!rect.height || rect.width < pageWidth * 0.8) return false;
      if (surface.dataset.blendFooterWhiteSurface === 'true') return true;
      const style = getComputedStyle(surface);
      const channels = style.backgroundColor.match(/[\d.]+/g)?.map(Number);
      if (style.backgroundImage !== 'none' || !channels || channels.length < 3) return false;
      const [r, g, b, alpha = 1] = channels;
      const white = alpha >= 0.99 && Math.min(r, g, b) >= 240 && Math.max(r, g, b) - Math.min(r, g, b) <= 10;
      if (white) surface.dataset.blendFooterWhiteSurface = 'true';
      return white;
    });
    // The home service section uses the main canvas, which may already be in its beige scroll state.
    const hasServiceCanvas = document.body.classList.contains('template-type-index') &&
      Boolean(mainContent.querySelector(':scope > .section--agency-services-projects'));
    cta.toggleAttribute('data-continues-white-canvas', hasWhiteSection || hasServiceCanvas);
  };

  const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => scheduleUpdate()) : null;

  const refresh = () => {
    refreshNeeded = false;
    resizeObserver?.disconnect();
    mainContent = document.getElementById('MainContent');
    entry = document.querySelector('.blend-footer-entry');
    cta = entry?.querySelector('[data-footer-cta]');
    footer = document.querySelector('.blend-footer');
    watermarkBottom = null;
    if (footer) resizeObserver?.observe(footer);
    if (!mainContent || !cta) {
      mainContent?.removeAttribute('data-blend-footer-transition');
      root.removeAttribute('data-blend-footer-focus');
      isActive = false;
      return;
    }
    mainContent.setAttribute('data-blend-footer-transition', '');
    syncCtaSurface();
    collectSurfaces();
    [mainContent, entry, cta].forEach((element) => resizeObserver?.observe(element));
  };

  const updateWatermark = (viewportTop, viewportBottom) => {
    if (!footer?.isConnected) return;
    let bottom = 24;
    if (!reducedMotion.matches) {
      const rect = footer.getBoundingClientRect();
      if (rect.top < viewportBottom && rect.bottom > viewportTop) {
        // Hold the logo in the viewport while the content scrolls over it,
        // then let it settle 24px above the footer's own bottom edge.
        bottom = Math.max(24, rect.bottom - viewportBottom + 24);
      }
    }
    bottom = Math.round(bottom * 100) / 100;
    if (bottom === watermarkBottom) return;
    watermarkBottom = bottom;
    footer.style.setProperty('--blend-footer-watermark-bottom', `${bottom}px`);
  };

  const update = () => {
    frameId = null;
    if (refreshNeeded) refresh();
    const viewportHeight = window.visualViewport?.height || root.clientHeight;
    const viewportTop = window.visualViewport?.offsetTop || 0;
    updateWatermark(viewportTop, viewportTop + viewportHeight);
    if (!mainContent || !cta?.isConnected) return;
    const rect = cta.getBoundingClientRect();
    // Stay active all the way through the footer; scrolling up restores the
    // existing page surface, including any active service-card canvas state.
    const nextActive = cta.getClientRects().length > 0 && rect.height > 0 && rect.top <= viewportHeight * 0.8;
    if (nextActive === isActive) return;
    if (nextActive) collectSurfaces();
    isActive = nextActive;
    root.toggleAttribute('data-blend-footer-focus', isActive);
    if (isActive) root.setAttribute('data-blend-footer-focus', 'true');
  };

  function scheduleUpdate() {
    if (frameId !== null) return;
    frameId = requestAnimationFrame(update);
  }

  const refreshAndUpdate = () => {
    refreshNeeded = true;
    scheduleUpdate();
  };

  ['scroll', 'resize', 'orientationchange', 'pageshow'].forEach((event) => {
    window.addEventListener(event, scheduleUpdate, { passive: true });
  });
  window.visualViewport?.addEventListener('resize', scheduleUpdate, { passive: true });
  window.visualViewport?.addEventListener('scroll', scheduleUpdate, { passive: true });
  reducedMotion.addEventListener('change', scheduleUpdate);
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((event) => {
    document.addEventListener(event, refreshAndUpdate);
  });
  window.addEventListener('load', refreshAndUpdate, { once: true });
  document.fonts?.ready.then(scheduleUpdate);
  window.blendFooterTransition = { refresh: refreshAndUpdate };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshAndUpdate, { once: true });
  } else {
    refreshAndUpdate();
  }
})();
